import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FileItem } from '@aposin/ng-aquila/file-uploader';
import { lastValueFrom } from 'rxjs';

import { environment } from '#environments/environment';
import { DocumentMetadata } from '#models/MicroController.model';

export interface DocumentPresignedUrl {
  presignedUrl: string;
  documentId: string;
}

export interface DocumentPostMetadata {
  fileName: string;
  mimeTypeCode: string;
}

function fileItemsToDocumentsMetadata(fileItems: FileItem[]): DocumentPostMetadata[] {
  return fileItems.map((fileItem) => ({
    fileName: fileItem.name,
    mimeTypeCode: fileItem.type,
  }));
}

@Injectable({
  providedIn: 'root',
})
export default class DocumentService {
  private readonly httpClient = inject(HttpClient);

  async createAndUploadDocumentsIfAny(
    iterationId: string,
    iterationBuCode?: string,
    fileItems?: FileItem[],
  ): Promise<string[] | undefined> {
    let documentPresignedUrls: DocumentPresignedUrl[] | undefined;

    if (fileItems?.length) {
      const createResult = await this.createDocuments(
        iterationId,
        iterationBuCode,
        fileItemsToDocumentsMetadata(fileItems),
      );
      documentPresignedUrls = createResult.documentPresignedUrls;

      for (let i = 0; i < documentPresignedUrls.length; i++) {
        await this.putDocument(documentPresignedUrls[i].presignedUrl, fileItems[i]);
      }

      return documentPresignedUrls.map((doc) => doc.documentId);
    }

    return undefined;
  }

  private async createDocuments(
    iterationId: string,
    iterationBuCode: string | undefined,
    documentsMetadata: { fileName: string; mimeTypeCode: string }[],
  ) {
    return lastValueFrom(
      this.httpClient.post<{ documentPresignedUrls: DocumentPresignedUrl[] }>(
        `${environment.customerVettingUrl}/iterations/${iterationId}/documents`,
        {
          userBusinessUnitCode: iterationBuCode,
          documentsMetadata,
        },
      ),
    );
  }

  async putDocument(presignedUrl: string, fileItem?: FileItem) {
    if (!fileItem) return;
    await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': fileItem.file?.type as string },
      credentials: 'include',
      body: fileItem.file,
    });
  }

  async getDocumentMetadata(dui: string) {
    return lastValueFrom(this.httpClient.get<DocumentMetadata>(`${environment.jadeUrl}/documents/${dui}/metadata`));
  }

  async downloadDocument(dui: string, fileName: string) {
    const { presignedUrl } = await lastValueFrom(
      this.httpClient.get<{ presignedUrl: string }>(`${environment.jadeUrl}/documents/${dui}`),
    );
    const res = await fetch(presignedUrl, {
      method: 'GET',
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const fakeLink = document.createElement('a');
    fakeLink.href = url;
    fakeLink.download = fileName;
    document.body.appendChild(fakeLink);
    fakeLink.click();
    fakeLink.remove();
  }
}
